import React from "react";

import classnames from "classnames";
import isEveryio from "Everyio/isEveryio";
import NextLogo from "images/logos/png/simplyinsured__next--white.png";
import SamsClubPartnerLogo from "images/logos/si_samsclub_topbar.png";
import SimplyInsuredLogo from "images/simplyinsured-logo-inverted-small.png";
import { capitalize } from "lodash";
import isPrimepay from "Primepay/isPrimepay";
import { Link } from "react-router-dom";
import isSquare from "Square/isSquare";
import { partnerType } from "Utils/isPartner";

import { NavBar, NavBarCenter, NavBarDropdownMenu, NavBarEnd, NavBarMenu, NavBarStart } from "Cobalt/NavBar";
import { usePartnerContext } from "Common/PartnerContext";
import { appendAuthParams } from "Common/TokenAuth/Utils";

import makeBem from "Utils/makeBem";

const bem = makeBem("ResponsiveTopBar");
const defaultLogoClassName = "mr-8 h-6 p-0 max-[500px]:mr-0 max-[500px]:h-auto max-[500px]:max-w-full";
const defaultLogoLinkClassName = "flex shrink-0 items-center hover:no-underline focus:no-underline";
const defaultNavBarClassName = [
  "left-0 top-0 z-[1050] mb-0 min-h-[var(--header-height)] w-full rounded-none border-0",
  "bg-[color:var(--primaryColor,#3e69b3)] px-8 py-4 text-white max-[500px]:px-4",
].join(" ");
const defaultDesktopMenuClassName = [
  "items-center bg-transparent p-0 list-none [&>li]:mr-6 [&>li:last-of-type]:mr-0",
  "[&>li>a]:cursor-pointer [&>li>a]:border-0 [&>li>a]:bg-transparent [&>li>a]:p-2 [&>li>a]:text-base",
  "[&>li>a]:!text-white [&>li>a]:no-underline hover:[&>li>a]:!text-white hover:[&>li>a]:!no-underline focus:[&>li>a]:!text-white",
  "[&>li>button]:cursor-pointer [&>li>button]:border-0 [&>li>button]:bg-transparent [&>li>button]:p-2 [&>li>button]:text-base",
  "[&>li>button]:!text-white [&>li>button]:no-underline hover:[&>li>button]:!text-white hover:[&>li>button]:!no-underline focus:[&>li>button]:!text-white",
  "[&>li>details>summary]:cursor-pointer [&>li>details>summary]:p-2 [&>li>details>summary]:text-base",
  "[&>li>details>summary]:!text-white [&>li>details>summary]:no-underline hover:[&>li>details>summary]:!text-white hover:[&>li>details>summary]:!no-underline",
  "[&>li>details>summary>span]:flex [&>li>details>summary>span]:max-w-[200px] [&>li>details>summary>span]:items-center [&>li>details>summary>span]:overflow-hidden [&>li>details>summary>span]:text-ellipsis [&>li>details>summary>span]:whitespace-nowrap",
  "[&>li>details>summary>span>span]:overflow-hidden [&>li>details>summary>span>span]:text-ellipsis [&>li>details>summary>span>span]:whitespace-nowrap",
  "[&>li>details>ul]:mt-3 [&>li>details>ul]:w-[200px] [&>li>details>ul]:text-gray-900 [&>li>details>ul>li]:mr-0",
].join(" ");
const defaultMobileMenuClassName = [
  "mb-0 [&>li]:mr-0",
  "[&>li>a]:px-3 [&>li>a]:py-2 [&>li>a]:leading-6 [&>li>a]:no-underline",
  "[&>li>button]:px-3 [&>li>button]:py-2 [&>li>button]:leading-6 [&>li>button]:no-underline",
  "[&>li>.dropdown-toggle]:px-3 [&>li>.dropdown-toggle]:py-2 [&>li>.dropdown-toggle]:leading-6 [&>li>.dropdown-toggle]:no-underline",
  "[&>li>details>summary]:px-3 [&>li>details>summary]:py-2 [&>li>details>summary]:leading-6 [&>li>details>summary]:no-underline",
  "[&>li>details>summary>span]:flex [&>li>details>summary>span]:max-w-[200px] [&>li>details>summary>span]:items-center [&>li>details>summary>span]:overflow-hidden [&>li>details>summary>span]:text-ellipsis [&>li>details>summary>span]:whitespace-nowrap",
  "[&>li>details>summary>span>span]:overflow-hidden [&>li>details>summary>span>span]:text-ellipsis [&>li>details>summary>span>span]:whitespace-nowrap",
  "[&>li>details>ul]:mt-3 [&>li>details>ul]:w-[200px] [&>li>details>ul]:text-gray-900 [&>li>details>ul>li]:mr-0",
].join(" ");
const defaultMobileMenuTriggerClassName = "mr-3 text-white hover:text-white focus:text-white lg:hidden";
const defaultMobileMenuDropdownClassName = "lg:hidden";

type Props = {
  leftContent?: Array<React.ReactNode>;
  rightContent?: Array<React.ReactNode>;
  branding?: string;
  collapsible?: boolean;
  collapseOnSelect?: boolean;
  partnerLogo?: string;
  partnerName?: string;
  ariaLabel?: string;
  logoLinkAriaLabel?: string;
};

export default function ResponsiveTopBar({
  ariaLabel = "Main navigation",
  branding,
  collapsible,
  collapseOnSelect: _collapseOnSelect,
  logoLinkAriaLabel,
  partnerLogo,
  partnerName,
  leftContent: initialLeftContent,
  rightContent: initialRightContent,
}: Props) {
  const { partnerLogo: contextPartnerLogo, partnerName: contextPartnerName } = usePartnerContext();

  const filterContent = (content) => {
    if (!content) return [];
    return content.filter((value) => value).map((item, key) => <React.Fragment key={key}>{item}</React.Fragment>);
  };

  const renderBrandImage = () => {
    let altText = "SimplyInsured";
    let src = SimplyInsuredLogo; // Assuming `SimplyInsuredLogo` is imported
    let isPartner = isSquare() || isEveryio() || isPrimepay();
    isPartner =
      isPartner ||
      branding === "7shifts" ||
      branding === "bamboo_hr" ||
      branding === "collective" ||
      branding === "everyio" ||
      branding === "housecall_pro" ||
      branding === "netsuite" ||
      branding === "primepay" ||
      branding === "remote" ||
      branding === "toast" ||
      branding === "vendasta" ||
      branding === "warp";

    // TODO(payroll_lead): add samsclub logo to S3 bucket
    if (branding === "samsclub") {
      altText = "SimplyInsured and Sam's Club";
      src = SamsClubPartnerLogo; // Assuming this is imported
      // TODO(payroll_lead): add next logo to S3 bucket
    } else if (branding === "next") {
      altText = "SimplyInsured and Next";
      src = NextLogo; // Assuming this is imported
    } else if (isPartner) {
      const brand = capitalize(branding || partnerType());
      altText = `SimplyInsured and ${brand}`;

      // TODO(payroll_lead): create 7shifts combined logo and add to S3 bucket
      // TODO(payroll_lead): add collective logo to S3 bucket
      src = `https://si-prod-logos.s3.us-east-1.amazonaws.com/${branding || brand?.toLowerCase()}/combined_light.png`;
    }

    if (partnerLogo) {
      src = partnerLogo;
      if (partnerName) {
        altText = `SimplyInsured and ${partnerName}`;
      }
    } else if (contextPartnerLogo) {
      src = contextPartnerLogo;
      altText = `SimplyInsured and ${contextPartnerName}`;
    }

    if (partnerType() === "square" && branding === "default" && !partnerLogo && !contextPartnerLogo) {
      // hyperspecificity for square
      return null; // avoid showing broken img
    }

    return <img className={classnames(bem("Logo"), defaultLogoClassName)} alt={altText} src={src} />;
  };

  const isCollapseValid = () => {
    const leftContent = filterContent(initialLeftContent);
    const rightContent = filterContent(initialRightContent);
    return (typeof collapsible === "undefined" || collapsible) && ((leftContent && leftContent.length > 0) || (rightContent && rightContent.length > 0));
  };

  const leftContent = filterContent(initialLeftContent);
  const rightContent = filterContent(initialRightContent);
  const mobileContent = filterContent([...leftContent, ...rightContent]);
  const shouldCollapse = isCollapseValid();
  const hasMobileContent = shouldCollapse && mobileContent.length > 0;
  const desktopOnlyClassName = shouldCollapse ? "max-lg:!hidden lg:flex" : "flex";

  return (
    <NavBar className={classnames(bem(), defaultNavBarClassName)} ariaLabel={ariaLabel}>
      <NavBarStart className="w-auto flex-none justify-start">
        {hasMobileContent && (
          <NavBarDropdownMenu
            trigger="Menu"
            triggerClassName={defaultMobileMenuTriggerClassName}
            menuClassName={defaultMobileMenuClassName}
            className={defaultMobileMenuDropdownClassName}
            triggerProps={{ "aria-label": "Open navigation menu" }}
          >
            {mobileContent}
          </NavBarDropdownMenu>
        )}
        <a href={appendAuthParams("/")} className={classnames(bem("LogoContainer"), defaultLogoLinkClassName)} aria-label={logoLinkAriaLabel}>
          {renderBrandImage()}
        </a>
      </NavBarStart>

      <NavBarCenter className={classnames("flex-auto justify-start", desktopOnlyClassName)}>
        {leftContent.length > 0 && <NavBarMenu className={defaultDesktopMenuClassName}>{leftContent}</NavBarMenu>}
      </NavBarCenter>

      <NavBarEnd className={classnames("flex-none", desktopOnlyClassName)}>
        {rightContent.length > 0 && <NavBarMenu className={defaultDesktopMenuClassName}>{rightContent}</NavBarMenu>}
      </NavBarEnd>
    </NavBar>
  );
}

type RoutedMenuItemProps = {
  children: React.ReactNode;
  to: string;
};

const RouterLink = Link as unknown as React.ComponentType<RoutedMenuItemProps & { role?: string }>;

export const RoutedMenuItem = ({ children, to }: RoutedMenuItemProps) => (
  <li role="presentation">
    <RouterLink to={to} role="menuitem">
      {children}
    </RouterLink>
  </li>
);
